import type { ReactNode } from 'react';
import classNames from 'classnames';

import Icon from '../Icon';

import type { IconClassName } from '../Icon';

export enum BadgeColors {
  Blue = 'blue',
  Cyan = 'cyan',
  Gray = 'gray',
  DarkGray = 'dark-gray',
  DarkGreen = 'dark-green',
  DarkRed = 'dark-red',
  Green = 'green',
  LightBlue = 'light-blue',
  LightGray = 'light-gray',
  LighterGray = 'lighter-gray', // For issue grouping & project dashboard badges
  LightGreen = 'light-green',
  LightPurple = 'light-purple',
  LightRed = 'light-red',
  LightYellow = 'light-yellow',
  Orange = 'orange',
  Pink = 'pink',
  Purple = 'purple',
  Red = 'red',
  Yellow = 'yellow',
  White = 'white',
}

export enum VulnBadgeColors {
  Critical = 'critical-severity-vuln',
  High = 'high-severity-vuln',
  Medium = 'medium-severity-vuln',
  Low = 'low-severity-vuln',
  Unknown = 'unknown-severity-vuln',
}

export enum AlertColors {
  Info = 'alert-info',
  Warning = 'alert-warning',
  Error = 'alert-error',
}

export enum BadgeShapes {
  Box = 'box',
  Circle = 'circle',
}

export enum BadgeSizes {
  Micro = 'micro',
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
}

export type BadgeProps = {
  /**
   * Background color of the badge
   */
  color: BadgeColors | AlertColors | VulnBadgeColors;
  /**
   * Optional icon to prepend the text in the badge
   */
  icon?: IconClassName;
  /**
   * The size of the badge, roughly matches button sizing @default BadgeSizes.Micro
   */
  size?: BadgeSizes;
  /**
   * The shape of the badge @default ColorBadgeShapes.Box
   */
  shape?: BadgeShapes;
  /**
   * Optional outline style.
   * Background becomes transparent and the provided color becomes foreground.
   */
  outline?: boolean;
  /**
   * Optional invert style - colors are inverted.
   * Light colors do not have inverts
   */
  invert?: boolean;
  /**
   * Fill the parent container.
   */
  fullWidth?: boolean;
  /**
   * Content to be displayed in the badge
   */
  children: ReactNode;
  /**
   * Optional title attribute for the badge. Useful when you have are displaying a count and are truncating the number.
   */
  title?: string;
};

/**
 * Generic badge with background color driven by @param color. The shape of the badge is by default a box shape, however can be made circular by explicitly passing in @param shape
 */
const Badge = ({
  color,
  icon,
  children,
  outline,
  invert,
  fullWidth,
  size = BadgeSizes.Micro,
  shape = BadgeShapes.Box,
  title,
}: BadgeProps) => (
  <div
    className={classNames(
      'color-badge',
      color,
      `color-badge__shape-${shape}`,
      {
        'color-badge__outline': outline,
        'color-badge__invert': invert || outline,
        'color-badge__size-micro': size === BadgeSizes.Micro,
        'color-badge__size-small': size === BadgeSizes.Small,
        'color-badge__size-medium': size === BadgeSizes.Medium,
        'color-badge__size-large': size === BadgeSizes.Large,
        'width-full': fullWidth,
      },
    )}
    title={title}
  >
    {icon && <div className="color-badge__icon"><Icon icon={icon} /></div>}
    {fullWidth ? <span className="width-full">{children}</span> : children}
  </div>
);

export default Badge;
